import { TESTER_MIN_AGE } from '@mogzo/shared';
import { Clock, Lock, ShieldCheck } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Avatar, Banner, CommunityIcon } from '../../components/community/Avatar';
import { CommunityShell, NoticeCard } from '../../components/community/Shell';
import { Alert, buttonClass, PageSpinner } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { cancelJoinRequest, fetchInvite, type Invite, joinWithInvite } from '../../lib/communities';
import { useSession } from '../../lib/session';

type State =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'failed' }
  | { status: 'ready'; invite: Invite };

const notice = 'mt-6 self-stretch rounded-xl bg-field px-4 py-3.5 text-left leading-relaxed';
const darkButton =
  'inline-flex min-h-12 w-full items-center justify-center rounded-full bg-ink px-6 text-[15px] font-semibold text-white hover:bg-ink-soft disabled:opacity-60';

function Point({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <li className="flex items-start gap-3 leading-[21px]">
      <span
        aria-hidden="true"
        className="flex size-9 shrink-0 items-center justify-center rounded-[11px] bg-field"
      >
        {icon}
      </span>
      <span className="pt-1.5">{children}</span>
    </li>
  );
}

/**
 * mogzo.com/join/<code>: an invite link to a private community, a game's test or a team
 * (M28). Opening it asks to join; a manager accepts. Testers must be adults.
 */
export function Join() {
  const { code = '' } = useParams();
  const { me } = useSession();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const here = `/join/${code}`;

  useEffect(() => {
    if (me === undefined) return;
    let active = true;
    fetchInvite(code)
      .then((invite) => active && setState({ status: 'ready', invite }))
      .catch((failed) => {
        if (!active) return;
        const missing = failed instanceof ApiError && failed.status === 404;
        setState({ status: missing ? 'missing' : 'failed' });
      });
    return () => {
      active = false;
    };
  }, [code, me]);

  // Members don't need the invite again.
  useEffect(() => {
    if (state.status === 'ready' && state.invite.viewer?.isMember) {
      const { kind, slug } = state.invite.community;
      navigate(`/${kind}/${slug}`, { replace: true });
    }
  }, [state, navigate]);

  function setRequested(requested: boolean) {
    setState((s) =>
      s.status === 'ready' && s.invite.viewer
        ? { ...s, invite: { ...s.invite, viewer: { ...s.invite.viewer, requested } } }
        : s,
    );
  }

  async function ask() {
    setBusy(true);
    setError(null);
    try {
      const answer = await joinWithInvite(code);
      if (answer.status === 'member') navigate(answer.path, { replace: true });
      else setRequested(true);
    } catch (failed) {
      if (failed instanceof ApiError && failed.code === 'invite_ended') {
        setState((s) =>
          s.status === 'ready' ? { ...s, invite: { ...s.invite, status: 'expired' } } : s,
        );
      } else {
        setError(failed instanceof Error ? failed.message : 'Couldn’t ask to join. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function takeBack() {
    setBusy(true);
    setError(null);
    try {
      await cancelJoinRequest(code);
      setRequested(false);
    } catch (failed) {
      setError(failed instanceof Error ? failed.message : 'Couldn’t take it back. Try again.');
    } finally {
      setBusy(false);
    }
  }

  if (state.status === 'loading' || me === undefined) return <PageSpinner />;
  if (state.status !== 'ready') {
    return (
      <CommunityShell>
        {state.status === 'missing' ? (
          <NoticeCard title="This invite link doesn’t work">
            <p>Check that you copied all of it, or ask whoever sent it for a new one.</p>
          </NoticeCard>
        ) : (
          <NoticeCard title="Couldn’t load this invite">
            <p>MOGZO didn’t answer. Reload the page to try again.</p>
          </NoticeCard>
        )}
      </CommunityShell>
    );
  }

  const { community, viewer, status, invitedBy, expiresAt, usesLeft } = state.invite;
  if (viewer?.isMember) return <PageSpinner />;
  const isTest = community.kind === 'test';
  const members = `${community.memberCount} member${community.memberCount === 1 ? '' : 's'}`;
  const until = new Date(expiresAt).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  let action: ReactNode;
  if (status !== 'active') {
    action = (
      <p className={notice}>
        <strong>This invite link no longer works.</strong> It expired, was used up, or a manager
        turned it off. Ask for a new one.
      </p>
    );
  } else if (!viewer) {
    action = (
      <div className="mt-auto flex flex-col gap-2.5 self-stretch pt-6">
        <Link to={`/sign-up?next=${encodeURIComponent(here)}`} className={darkButton}>
          Create an account
        </Link>
        <Link
          to={`/sign-in?next=${encodeURIComponent(here)}`}
          className={`${buttonClass('secondary')} min-h-12`}
        >
          I have an account: sign in
        </Link>
        <p className="mt-1 text-[13px] text-muted">You come back here afterwards to ask to join.</p>
      </div>
    );
  } else if (isTest && !viewer.oldEnough) {
    action = (
      <p className={notice}>
        <strong>Testers need to be {TESTER_MIN_AGE} or older.</strong> Test builds haven’t been
        reviewed by MOGZO yet. You’ll be able to play {community.name} when it’s published.
      </p>
    );
  } else if (viewer.requested) {
    action = (
      <div className="mt-auto self-stretch pt-6">
        <div role="status" className="rounded-2xl border border-[#f3dfb5] bg-cream px-4 py-3.5">
          <p className="font-semibold">Request sent</p>
          <p className="mt-0.5 text-sm leading-relaxed text-muted">
            A manager will let you in. Then {community.name} shows in your library.
          </p>
        </div>
        <button
          type="button"
          onClick={takeBack}
          disabled={busy}
          className="mt-3 text-sm font-semibold text-muted underline hover:text-ink"
        >
          {busy ? 'Taking it back…' : 'Take back the request'}
        </button>
      </div>
    );
  } else {
    action = (
      <div className="mt-auto self-stretch pt-6">
        <button type="button" onClick={ask} disabled={busy} className={darkButton}>
          {busy ? 'Asking…' : 'Ask to join'}
        </button>
        <p className="mt-3 text-[13px] text-muted">
          Managers see your name and username, not your email.
        </p>
      </div>
    );
  }

  return (
    <CommunityShell>
      <main
        aria-labelledby="join-title"
        className="mx-auto flex w-full max-w-[520px] flex-col sm:px-6 sm:pt-10 sm:pb-16"
      >
        <div className="flex flex-col overflow-hidden bg-card sm:rounded-3xl sm:border sm:border-line">
          <Banner className="h-[150px]" />
          <div className="flex flex-1 flex-col items-center px-6 pb-7 text-center">
            <span className="relative z-10 -mt-12">
              <CommunityIcon name={community.name} />
            </span>
            {invitedBy && (
              <p className="mt-4 inline-flex items-center gap-2 text-sm text-muted">
                <Avatar name={invitedBy} size="xs" />
                {invitedBy} invited you to
              </p>
            )}
            <h1
              id="join-title"
              className="mt-1.5 text-[26px] leading-8 font-bold tracking-[-0.01em]"
            >
              {community.name}
            </h1>
            <div className="mt-2.5 flex items-center gap-2">
              <span className="inline-flex h-6 items-center gap-1.5 rounded-full border border-[#f3dfb5] bg-cream px-2.5 text-xs font-semibold">
                <Lock size={12} strokeWidth={2.4} aria-hidden="true" />
                {isTest ? 'Private test' : 'Private team'}
              </span>
              <span className="text-[13px] text-muted">{members}</span>
            </div>
            {isTest && community.developerName && (
              <p className="mt-2 text-sm text-muted">
                by {community.developerName}. Play the game before it’s published.
              </p>
            )}
            {!isTest && community.about && (
              <p className="mt-2 text-sm leading-relaxed text-muted">{community.about}</p>
            )}

            {status === 'active' && (
              <ul className="mt-6 flex flex-col gap-3.5 self-stretch text-left">
                <Point icon={<ShieldCheck size={18} strokeWidth={1.9} />}>
                  A manager accepts or declines each request. Only members see it and its posts.
                </Point>
                <Point icon={<Clock size={18} strokeWidth={1.9} />}>
                  This link works until {until}, for {usesLeft} more{' '}
                  {usesLeft === 1 ? 'person' : 'people'}.
                </Point>
              </ul>
            )}

            {error && (
              <div className="mt-4 self-stretch text-left">
                <Alert>{error}</Alert>
              </div>
            )}
            {action}
          </div>
        </div>
      </main>
    </CommunityShell>
  );
}
